import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { TrendingUp, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const verdictColor = {
  'STRONG BUY': 'bg-emerald-600',
  'BUY': 'bg-green-600',
  'HOLD': 'bg-amber-500',
  'AVOID': 'bg-red-600',
};

const formatEuro = (n) =>
  typeof n === 'number'
    ? new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n)
    : n;

const InvestmentAnalysis = ({ property }) => {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);

  const runAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/ai/investment-analysis`, {
        property_type: property.propertyType || property.type || 'house',
        bedrooms: property.bedrooms || 0,
        bathrooms: property.bathrooms || 0,
        size: property.size || 0,
        location: property.location?.area || property.location?.fullAddress || 'Dublin',
        asking_price: property.price || 0,
        listing_type: property.type || 'sale',
        country: property.location?.country || null,
      });
      setAnalysis(data.analysis);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="investment-analysis">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-[#7042cc]" />
          AI Investment Analysis
        </CardTitle>
        <CardDescription>
          Institutional-grade report: fair value range, 5-year forecast, rental yield, risk score and verdict.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!analysis && !loading && (
          <Button
            onClick={runAnalysis}
            data-testid="run-investment-analysis-btn"
            className="bg-[#7042cc] hover:bg-[#5b35a8] text-white"
          >
            Run investment analysis
          </Button>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-gray-600" data-testid="investment-analysis-loading">
            <Loader2 className="h-5 w-5 animate-spin" />
            Running analysis… this takes ~10 seconds
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {analysis && !analysis.error && (
          <div className="space-y-4" data-testid="investment-analysis-results">
            {/* Verdict banner */}
            <div className="flex items-center gap-3">
              <Badge
                className={`${verdictColor[analysis.investment_verdict] || 'bg-gray-600'} text-white px-3 py-1 text-sm`}
                data-testid="investment-verdict"
              >
                {analysis.investment_verdict}
              </Badge>
              <span className="text-sm text-gray-700">{analysis.verdict_rationale}</span>
            </div>

            {/* Metric grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric
                label="Fair value low"
                value={formatEuro(analysis.fair_value_low)}
                testid="metric-fair-low"
              />
              <Metric
                label="Fair value high"
                value={formatEuro(analysis.fair_value_high)}
                testid="metric-fair-high"
              />
              <Metric
                label="5-yr forecast"
                value={`${analysis.five_year_forecast_pct > 0 ? '+' : ''}${analysis.five_year_forecast_pct}%`}
                testid="metric-forecast"
              />
              <Metric
                label="Risk score"
                value={`${analysis.risk_score} / 10`}
                testid="metric-risk"
              />
              <Metric
                label="Est. monthly rent"
                value={formatEuro(analysis.estimated_monthly_rent)}
                testid="metric-rent"
              />
              <Metric
                label="Gross yield"
                value={`${analysis.gross_rental_yield_pct}%`}
                testid="metric-gross-yield"
              />
              <Metric
                label="Net yield (after 25% costs)"
                value={`${analysis.net_rental_yield_pct}%`}
                testid="metric-net-yield"
              />
            </div>

            <div className="text-sm text-gray-700" data-testid="fair-value-comment">
              {analysis.fair_value_comment}
            </div>
            <div className="text-sm text-gray-700" data-testid="forecast-comment">
              {analysis.five_year_forecast_comment}
            </div>

            {/* Risk factors */}
            {Array.isArray(analysis.risk_factors) && analysis.risk_factors.length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Risk factors</div>
                <ul className="space-y-1">
                  {analysis.risk_factors.map((r, i) => (
                    <li key={i} className="text-sm text-gray-700 flex gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-start gap-2 text-xs text-gray-500 pt-2 border-t border-gray-100">
              <CheckCircle2 className="h-4 w-4 text-[#7042cc] flex-shrink-0 mt-0.5" />
              AI-generated analysis for informational purposes only. Not financial advice. Always perform
              independent due diligence before purchasing.
            </div>
          </div>
        )}

        {analysis?.error && (
          <div className="text-sm text-red-700">{analysis.error}</div>
        )}
      </CardContent>
    </Card>
  );
};

const Metric = ({ label, value, testid }) => (
  <div className="rounded-md border border-gray-200 bg-white p-3">
    <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</div>
    <div className="text-base font-semibold text-gray-900" data-testid={testid}>{value}</div>
  </div>
);

export default InvestmentAnalysis;
